/* Web Push subscription helper — Feb 2026 */
import axios from 'axios';
const API_URL = process.env.REACT_APP_BACKEND_URL;
const VAPID_PUBLIC_KEY_ENV = process.env.REACT_APP_VAPID_PUBLIC_KEY;

function urlBase64ToUint8Array(b64) {
  const padding = '='.repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

/**
 * Resolve the VAPID public key. Prefer the build-time env var
 * (REACT_APP_VAPID_PUBLIC_KEY). If absent, fall back to the public
 * /api/push/vapid-public-key endpoint so the feature still works
 * during local development or after env mis-configuration.
 */
async function resolveVapidPublicKey() {
  if (VAPID_PUBLIC_KEY_ENV && VAPID_PUBLIC_KEY_ENV.length > 20) {
    return VAPID_PUBLIC_KEY_ENV;
  }
  try {
    const r = await axios.get(`${API_URL}/api/push/vapid-public-key`);
    return r.data?.key || null;
  } catch (_) {
    return null;
  }
}

export async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return null;
  try {
    return await navigator.serviceWorker.register('/sw.js');
  } catch (e) {
    return null;
  }
}

/** Read-only readiness check. Never opens a browser permission prompt. */
export async function getPushSubscriptionStatus() {
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || typeof Notification === 'undefined') {
    return { supported: false, permission: 'unsupported', subscribed: false };
  }
  const reg = await registerServiceWorker();
  if (!reg) return { supported: false, permission: Notification.permission, subscribed: false };
  await navigator.serviceWorker.ready;
  const subscription = await reg.pushManager.getSubscription();
  return {
    supported: true,
    permission: Notification.permission,
    subscribed: Boolean(subscription),
  };
}

export async function ensurePushSubscription() {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return { ok: false, reason: 'unsupported' };

  const vapidKey = await resolveVapidPublicKey();
  if (!vapidKey) return { ok: false, reason: 'no_key' };

  const reg = await registerServiceWorker();
  if (!reg) return { ok: false, reason: 'sw_failed' };

  // Wait until SW is ready / activated to be safe
  await navigator.serviceWorker.ready;

  // Permission flow
  let perm = Notification.permission;
  if (perm === 'default') perm = await Notification.requestPermission();
  if (perm !== 'granted') return { ok: false, reason: 'permission_denied' };

  // Subscribe — if a stale subscription exists with a different applicationServerKey
  // it would throw. Recover by unsubscribing and resubscribing.
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    try {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      });
    } catch (err) {
      // InvalidStateError or different applicationServerKey — clear and retry.
      try {
        const existing = await reg.pushManager.getSubscription();
        if (existing) await existing.unsubscribe();
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidKey),
        });
      } catch (err2) {
        return { ok: false, reason: 'subscribe_failed', error: err2?.message || String(err2) };
      }
    }
  }

  const subJson = sub.toJSON();
  try {
    await axios.post(`${API_URL}/api/push/subscribe`, {
      endpoint: subJson.endpoint,
      keys: subJson.keys,
    }, { withCredentials: true });
  } catch (err) {
    return { ok: false, reason: 'persist_failed', error: err?.response?.data?.detail || err?.message };
  }
  return { ok: true };
}
